import React, { useState } from 'react'
import {
    Box,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import * as datefns from 'date-fns'
import { UserDeviceEntity } from '@finance-lab/utils/types'
import { getDeviceDetailsJson, parseUserDeviceDetails } from './userDeviceDisplay'

function WithUserAgentTooltip({ children, userAgent }: { children: React.ReactNode; userAgent: string | null }) {
    if (!userAgent) {
        return <>{children}</>
    }
    return (
        <Tooltip title={userAgent} placement='top-start' enterDelay={400}>
            <span>{children}</span>
        </Tooltip>
    )
}

function DeviceDetailsPanel({ device }: { device: UserDeviceEntity }) {
    return (
        <Box sx={{ py: 1, px: 1 }}>
            <Box
                component='pre'
                sx={{
                    m: 0,
                    p: 2,
                    fontSize: '0.75rem',
                    lineHeight: 1.45,
                    overflow: 'auto',
                    maxHeight: 360,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                }}
            >
                {getDeviceDetailsJson(device.details)}
            </Box>
        </Box>
    )
}

export function UserDevicesTable({ devices }: { devices: UserDeviceEntity[] }) {
    const [expandedDeviceId, setExpandedDeviceId] = useState<string | null>(null)

    if (devices.length === 0) {
        return <Typography color='text.secondary'>Inga enheter registrerade ännu.</Typography>
    }

    return (
        <Table size='small'>
            <TableHead>
                <TableRow>
                    <TableCell padding='checkbox' />
                    <TableCell>Enhets-ID</TableCell>
                    <TableCell>Enhet</TableCell>
                    <TableCell>Operativsystem</TableCell>
                    <TableCell>Webbläsare</TableCell>
                    <TableCell>IP</TableCell>
                    <TableCell>Senast sedd</TableCell>
                    <TableCell>Första gången</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {devices.map((device) => {
                    const parsed = parseUserDeviceDetails(device.details)
                    const isExpanded = expandedDeviceId === device.deviceId
                    return (
                        <React.Fragment key={device.deviceId}>
                            <TableRow hover>
                                <TableCell padding='checkbox'>
                                    <IconButton
                                        size='small'
                                        aria-label={isExpanded ? 'Dölj details' : 'Visa details'}
                                        onClick={() => setExpandedDeviceId(isExpanded ? null : device.deviceId)}
                                    >
                                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    </IconButton>
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                    {device.deviceId}
                                </TableCell>
                                <TableCell>
                                    <WithUserAgentTooltip userAgent={parsed.userAgent}>
                                        {parsed.deviceKind ?? '—'}
                                    </WithUserAgentTooltip>
                                </TableCell>
                                <TableCell>{parsed.os ?? '—'}</TableCell>
                                <TableCell>{parsed.browser ?? '—'}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                    {parsed.ipAddress ?? '—'}
                                </TableCell>
                                <TableCell>{datefns.format(new Date(device.lastSeen), 'yyyy-MM-dd HH:mm')}</TableCell>
                                <TableCell>{datefns.format(new Date(device.createdAt), 'yyyy-MM-dd HH:mm')}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={8} sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}>
                                    <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                                        <DeviceDetailsPanel device={device} />
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        </React.Fragment>
                    )
                })}
            </TableBody>
        </Table>
    )
}
